import React, { useEffect, useMemo, useState, useContext } from "react";
import axiosInstance from "../../axiosInstance";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import WarningIcon from "@mui/icons-material/Warning";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Table from "../Theme/Table.js";
import Spinner from "../Spinner";
import ActionButtons from "../ActionButton.js";
import { RoleContext } from "../RoleContext";

const AcceptedTripsList = ({ socket }) => {
  const roleData = useContext(RoleContext);
  const role = JSON.parse(roleData);
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    emergencyGroupId: null,
    tripRequestId: null,
  });
  const [cancelReason, setCancelReason] = useState("");

  const getAcceptedTripRequests = async () => {
    try {
      const res = await axiosInstance.get("/acceptedTripRequests");
      setGroups(res.data?.groups || []);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error("Something went wrong ...");
    }
  };

  useEffect(() => {
    getAcceptedTripRequests();
  }, []);

  const rows = useMemo(() => {
    return (groups || []).map((g) => {
      const items = g.items || [];
      const passenger = g.passenger || items[0]?.passenger || items[0]?.Passenger || null;
      const drivers = items
        .map((i) => i.driver || i.Driver || null)
        .filter(Boolean);

      const first = items[0] || {};

      return {
        key: g.key,
        emergencyGroupId: g.groupId,
        tripRequestId: first.id,
        passenger,
        drivers,
        pickUpLocationLatitude: first.pickUpLocationLatitude,
        pickUpLocationLongitude: first.pickUpLocationLongitude,
        destinationLocationLatitude: first.destinationLocationLatitude,
        destinationLocationLongitude: first.destinationLocationLongitude,
        distance: first.distance,
        estimatedPrice: first.estimatedPrice,
        status: g.status || "Accepted",
        createdAt: g.createdAt,
        updatedAt: g.updatedAt,
        itemsCount: items.length,
      };
    });
  }, [groups]);

  const handleCancel = async () => {
    try {
      if (!cancelReason || cancelReason.trim() === "") {
        toast.error("Please provide a reason for cancelling this trip");
        return;
      }

      if (!cancelDialog.emergencyGroupId && !cancelDialog.tripRequestId) {
        toast.error("Missing group id / request id");
        return;
      }

      await axiosInstance.post("/acceptedTripRequests/cancel", {
        emergencyGroupId: cancelDialog.emergencyGroupId || undefined,
        tripRequestId: cancelDialog.tripRequestId || undefined,
        reasonForCancellation: cancelReason.trim(),
      });

      if (socket && socket.connected) {
        socket.emit("broadcastCancellation", {
          emergencyGroupId: cancelDialog.emergencyGroupId || undefined,
          tripRequestId: cancelDialog.tripRequestId || undefined,
          reasonForCancellation: cancelReason.trim(),
        });
      }

      toast.success("Trip cancelled");
      setCancelDialog({ open: false, emergencyGroupId: null, tripRequestId: null });
      setCancelReason("");
      setLoading(true);
      await getAcceptedTripRequests();
    } catch (err) {
      toast.error("Failed to cancel trip");
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Group",
        columns: [
          {
            Header: "Emergency Group ID",
            accessor: "emergencyGroupId",
            Cell: (row) => {
              const id = row.cell.row.original.emergencyGroupId;
              return id ? (
                <Tooltip title={id} placement="top">
                  <span
                    style={{
                      display: "block",
                      maxWidth: "180px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                    }}
                  >
                    {id}
                  </span>
                </Tooltip>
              ) : (
                <span style={{ color: "#9ca3af", fontStyle: "italic" }}>Single</span>
              );
            },
          },
          {
            Header: "Requests",
            accessor: "itemsCount",
          },
        ],
      },
      {
        Header: "Passenger",
        columns: [
          {
            Header: "Name",
            accessor: "passenger.firstName",
            Cell: (row) => {
              const p = row.cell.row.original.passenger;
              if (!p) return <span style={{ color: "#9ca3af", fontStyle: "italic" }}>N/A</span>;
              return `${p.firstName || ""} ${p.lastName || ""}`.trim();
            },
          },
          {
            Header: "Phone",
            accessor: "passenger.phoneNumber",
            Cell: (row) => {
              const p = row.cell.row.original.passenger;
              return p?.phoneNumber || "N/A";
            },
          },
        ],
      },
      {
        Header: "Drivers",
        columns: [
          {
            Header: "Count",
            accessor: "driversCount",
            Cell: (row) => {
              const drivers = row.cell.row.original.drivers || [];
              return drivers.length;
            },
          },
        ],
      },
      {
        Header: "Status",
        accessor: "status",
        disableFilters: true,
        Cell: (row) => {
          const status = row.cell.row.original.status || "Accepted";
          return (
            <Chip
              label={status}
              color={status === "Accepted" ? "info" : status === "Cancelled" ? "error" : "default"}
              variant="filled"
              sx={{
                fontWeight: "bold",
                borderRadius: "8px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            />
          );
        },
      },
      {
        Header: "Action",
        accessor: "action",
        disableFilters: true,
        Cell: (row) => {
          const emergencyGroupId = row.cell.row.original.emergencyGroupId;
          const tripRequestId = row.cell.row.original.tripRequestId;

          const canCancel = role?.some((e) => e.perm_name === "list_active_trips");
          const canView = role?.some((e) => e.perm_name === "list_active_trips");

          return (
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              {canView && (
                <Tooltip title="View">
                  <span>
                    <VisibilityIcon
                      color="success"
                      sx={{
                        cursor: "pointer",
                        transition: "transform 0.2s",
                        "&:hover": {
                          transform: "scale(1.2)",
                          color: "#16a34a",
                        },
                      }}
                      onClick={() => {
                        if (tripRequestId) navigate(`/tripRequestDetail/${tripRequestId}`);
                      }}
                    />
                  </span>
                </Tooltip>
              )}

              {canCancel && (
                <button
                  onClick={() => {
                    setCancelDialog({
                      open: true,
                      emergencyGroupId: emergencyGroupId || null,
                      tripRequestId: emergencyGroupId ? null : tripRequestId,
                    });
                    setCancelReason("");
                  }}
                  style={{
                    backgroundColor: "#b91c1c",
                    color: "white",
                    padding: "8px 16px",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "500",
                    transition: "background-color 0.2s",
                    whiteSpace: "nowrap",
                    minWidth: "110px",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [role, navigate]
  );

  return (
    <div className="content-wrappers" style={{ marginTop: "-40px" }}>
      <div className="content-wrappers">
        <br />
        <br />
        <ToastContainer />

        <Dialog
          open={cancelDialog.open}
          onClose={() => {
            setCancelDialog({ open: false, emergencyGroupId: null, tripRequestId: null });
            setCancelReason("");
          }}
          PaperProps={{
            style: {
              borderRadius: "12px",
              padding: "16px",
              maxWidth: "450px",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              pb: 1,
              "& .MuiTypography-root": {
                fontSize: "1.5rem",
                fontWeight: 600,
                color: "#b91c1c",
              },
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
              <WarningIcon sx={{ color: "#b91c1c", fontSize: 28 }} />
              <Typography>Cancel Accepted Trip</Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ pb: 3 }}>
            <Typography
              align="center"
              sx={{
                fontSize: "1rem",
                color: "#4b5563",
                mt: 1,
                mb: 2,
              }}
            >
              This will cancel all accepted trip requests in the emergency group.
            </Typography>

            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: "600", mb: 1, color: "#374151" }}>
                Reason for Cancellation <span style={{ color: "#ef4444" }}>*</span>
              </Typography>
              <textarea
                placeholder="e.g., Duplicate request, wrong assignment, etc."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: cancelReason.trim() ? "2px solid #10b981" : "2px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "0.95rem",
                  outline: "none",
                  resize: "vertical",
                  fontFamily: "inherit",
                }}
              />
              {!cancelReason.trim() && (
                <Typography sx={{ fontSize: "0.75rem", color: "#ef4444", mt: 0.5 }}>
                  This field is required
                </Typography>
              )}
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              padding: "16px",
              borderTop: "1px solid #e5e7eb",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Button
              onClick={() => {
                setCancelDialog({ open: false, emergencyGroupId: null, tripRequestId: null });
                setCancelReason("");
              }}
              sx={{
                color: "#6b7280",
                backgroundColor: "#f3f4f6",
                padding: "8px 24px",
                "&:hover": {
                  backgroundColor: "#e5e7eb",
                },
              }}
            >
              Close
            </Button>
            <Button
              onClick={handleCancel}
              sx={{
                backgroundColor: "#b91c1c",
                color: "white",
                padding: "8px 24px",
                "&:hover": {
                  backgroundColor: "#991b1b",
                },
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        <section className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <ActionButtons title="Accepted Trips" is_all_trips={false} />
                  <div className="card-body">
                    {loading ? (
                      <div>
                        <Spinner />
                      </div>
                    ) : (
                      <Table columns={columns} data={rows} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AcceptedTripsList;
